/**
 * Prompt templates for ranking places.
 * Data-focused: context, rules, output format.
 * Creative voice comes from system.ts persona + hook rules.
 */

import type { Place } from '@/hooks/usePlaces';
import { getOpenStatusSummary } from '@/utils/openingHoursInference';
import { CONCIERGE_PERSONA, HOOK_RULES } from './system';
import { estimateTravelMins } from '@/utils/travelTime';

// ─── Helpers ────────────────────────────────────────────────────────────────

const VIBE_LABELS: Record<string, string> = {
  hungry: 'food, restaurants, cafes',
  'must-sees': 'major attractions, museums',
  adventurous: 'active, outdoor, unique experiences',
  'hidden-gems': 'local favorites, lesser-known spots',
  popular: 'famous, well-known places (1000+ reviews)',
  'feeling-lucky': 'nightlife, entertainment, surprise me',
};

const TIME_LABELS: Record<string, string> = {
  '1-2-hrs': '1-2 hours',
  'half-day': 'half a day',
  'full-day': 'a full day',
  '2-3-days': '2-3 days',
};

const TIME_MINUTES: Record<string, number> = {
  '1-2-hrs': 90,
  'half-day': 240,
  'full-day': 480,
  '2-3-days': 1440,
};

function getLanguageName(code: string): string {
  const names: Record<string, string> = { en: 'English', fr: 'French', es: 'Spanish', de: 'German' };
  return names[code] || 'English';
}

function getTimeOfDay(hour: number): string {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

// ─── Place context builders ─────────────────────────────────────────────────

interface PlaceContextLow {
  idx: number;
  name: string;
  rating: number | undefined;
  reviews: number | undefined;
  types: string[];
  summary: string | undefined;
  openStatus: string;
  travelMins: number | undefined;
  placeType: string | undefined;
  features: string | undefined;
}

interface PlaceContextMedium extends PlaceContextLow {
  priceLevel: string | undefined;
  distanceMeters: number | undefined;
  travelTimeText: string | undefined;
  placeType: string | undefined;  // human-readable type e.g. "Italian Restaurant"
  features: string | undefined;   // compact feature string e.g. "dine-in, outdoor seating, serves wine"
}

function buildLowPlaceContext(places: Place[], currentHour: number): PlaceContextLow[] {
  return places.map((p, idx) => {
    const travelMins = p.routingDurationSeconds
      ? Math.round(p.routingDurationSeconds / 60)
      : undefined;
    return {
      idx,
      name: p.name,
      rating: p.rating,
      reviews: p.userRatingCount,
      types: p.types.slice(0, 5),
      summary: p.editorialSummary,
      openStatus: getOpenStatusSummary(p.currentOpeningHours, p.types, currentHour, travelMins ?? 0),
      travelMins,
      placeType: p.primaryTypeDisplayName,
      features: buildFeatures(p),
    };
  });
}

/** Build a compact features string from place service attributes */
function buildFeatures(p: Place): string | undefined {
  const features: string[] = [];
  if (p.dineIn) features.push('dine-in');
  if (p.takeout) features.push('takeout');
  if (p.delivery) features.push('delivery');
  if (p.outdoorSeating) features.push('outdoor seating');
  if (p.reservable) features.push('reservable');
  if (p.liveMusic) features.push('live music');
  if (p.servesBeer || p.servesWine) features.push(p.servesBeer && p.servesWine ? 'beer & wine' : p.servesBeer ? 'beer' : 'wine');
  if (p.servesBreakfast) features.push('breakfast');
  if (p.servesLunch) features.push('lunch');
  if (p.servesDinner) features.push('dinner');
  if (p.goodForChildren) features.push('kid-friendly');
  if (p.goodForGroups) features.push('good for groups');
  return features.length > 0 ? features.join(', ') : undefined;
}

function buildMediumPlaceContext(
  places: Place[],
  currentHour: number,
  realTravelTimes: Map<string, { mins: number; text: string }>,
  transport: string
): PlaceContextMedium[] {
  return places.map((p, idx) => {
    const realTime = realTravelTimes.get(p.id);
    const travelMins = realTime
      ? realTime.mins
      : p.distanceMeters
        ? estimateTravelMins(p.distanceMeters, transport)
        : undefined;

    return {
      idx,
      name: p.name,
      rating: p.rating,
      reviews: p.userRatingCount,
      types: p.types.slice(0, 5),
      summary: p.editorialSummary,
      openStatus: getOpenStatusSummary(p.currentOpeningHours, p.types, currentHour, travelMins ?? 0),
      travelMins,
      priceLevel: p.priceLevel,
      distanceMeters: p.distanceMeters,
      travelTimeText: realTime?.text,
      placeType: p.primaryTypeDisplayName,
      features: buildFeatures(p),
    };
  });
}

// ─── Vibe description ───────────────────────────────────────────────────────

function describeVibes(vibes: string[]): string {
  const lines = vibes.map(v => {
    if (v.startsWith('custom:')) {
      const label = v.slice(7);
      return `- "${label}": user specifically searched for this — prioritize these results`;
    }
    return `- ${v}: ${VIBE_LABELS[v] || v}`;
  });
  return lines.join('\n');
}

// ─── Traveller summary (for persona grounding) ─────────────────────────────

function buildTravellerSummary(
  location: string,
  transport: string,
  timeAvailable: string,
  vibes: string[],
  currentHour: number,
  tasteHint?: string | null,
  weatherLine?: string | null
): string {
  const timeLabel = TIME_LABELS[timeAvailable] || timeAvailable;
  const timeOfDay = getTimeOfDay(currentHour);

  let summary = `RIGHT NOW:
- In: ${location}
- Has: ${timeLabel} (it's ${currentHour}:00, ${timeOfDay})
- Getting around by: ${transport}
- Looking for: ${vibes.map(v => v.startsWith('custom:') ? `"${v.slice(7)}"` : v).join(', ')}`;

  if (weatherLine) {
    summary += `\n- Weather: ${weatherLine}`;
  }

  if (tasteHint) {
    summary += `\n\nWHAT YOU KNOW ABOUT THIS TRAVELLER:\n${tasteHint}`;
  }

  return summary;
}

// ─── Prompt builders ────────────────────────────────────────────────────────

export function buildLowPrompt(
  places: Place[],
  location: string,
  vibes: string[],
  language: string,
  transport: string,
  timeAvailable: string,
  tasteHint?: string | null,
  weatherLine?: string | null
): { system: string; user: string } {
  const currentHour = new Date().getHours();
  const placeContext = buildLowPlaceContext(places, currentHour);
  const outputLanguage = getLanguageName(language);

  const system = CONCIERGE_PERSONA;

  const user = `${buildTravellerSummary(location, transport, timeAvailable, vibes, currentHour, tasteHint, weatherLine)}

VIBES:
${describeVibes(vibes)}

PLACES (${places.length}):
${JSON.stringify(placeContext)}

TASK: Rank these places and, for each, tell the traveller — as "you" — why it made their list (or why it's a weaker pick). Output in ${outputLanguage}.

For each place return:
- idx: index from input
- hook: the description, shaped as set out under DESCRIPTION — LENGTH AND SHAPE below; use summary, features, placeType, rating, distance and openStatus to make a specific case
- matchingVibes: which vibes from [${vibes.join(', ')}] this matches

RANKING: Closest high-rated places first. Practicality over perfection.
- If summary exists, USE it — it has specific details (cuisine, atmosphere, features)
- Prefer closer places. If a great place is far, suggest taxi in the hook.
- If openStatus says "closed" or "likely closed", say so immediately in the hook.

${HOOK_RULES}

Return a JSON object with one key, "results", holding one entry per place. No markdown, no explanation.`;

  return { system, user };
}

export function buildMediumPrompt(
  places: Place[],
  location: string,
  timeAvailable: string,
  transport: string,
  vibes: string[],
  language: string,
  realTravelTimes: Map<string, { mins: number; text: string }>,
  tasteHint?: string | null,
  weatherLine?: string | null
): { system: string; user: string } {
  const currentHour = new Date().getHours();
  const placeContext = buildMediumPlaceContext(places, currentHour, realTravelTimes, transport);
  const outputLanguage = getLanguageName(language);
  const timeLabel = TIME_LABELS[timeAvailable] || timeAvailable;
  const availableMins = TIME_MINUTES[timeAvailable] || 240;
  const maxPlaces = Math.min(places.length, 20);
  const placesPerVibe = Math.max(4, Math.ceil(maxPlaces / vibes.length));

  const system = CONCIERGE_PERSONA;

  const user = `${buildTravellerSummary(location, transport, timeAvailable, vibes, currentHour, tasteHint, weatherLine)}

VIBES:
${describeVibes(vibes)}

PLACES (${places.length} candidates):
${JSON.stringify(placeContext)}

TASK: Rank the best options and, for each, tell the traveller — as "you" — why it earned its position on their list. Output in ${outputLanguage}.
You're writing a ranked list — reference other places by position number when comparing. Use each place's summary, features, placeType, and the traveller's constraints to build a specific case.

For each place return:
- idx: numeric index from input (must match exactly)
- hook: the description, shaped as set out under DESCRIPTION — LENGTH AND SHAPE below; use summary, features, placeType, rating, distance and openStatus. Never a bare verdict — prove each reason with specifics from the data.
- estimatedDuration: time to spend, as a short range in the output language
- category: short label from types
- distanceNote: use travelTimeText if available (real Google Maps data), otherwise estimate from travelMins
- matchingVibes: which vibes from [${vibes.join(', ')}] this matches
- practicalityRank: 1-10 (10 = most practical for this traveller's situation)

RANKING PRIORITY:
1. OPEN STATUS: "open (confirmed)" and "likely open" = good. "closed" = rank last. "hours unknown" = neutral.
2. TIME BUDGET: ${availableMins} mins total. A place with travelMins=40 on foot = 80 mins round-trip = most of your ${timeLabel} gone just walking. Factor this honestly.
3. QUALITY: A 4.2 that's 5 mins away beats a 4.5 that's 45 mins away when time is limited.
4. BALANCE: Include at least ${placesPerVibe} places per vibe: ${vibes.join(', ')}.

Return UP TO ${maxPlaces} places in the "results" array.

IMPORTANT: Each output entry must match exactly ONE input place. Never merge or mix places.

${HOOK_RULES}

Return a JSON object with one key, "results", holding the ranked entries. No markdown, no explanation.`;

  return { system, user };
}
